import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Twitter, Instagram, Youtube, Twitch, Globe, Gamepad2, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CreationCard } from "@/components/CreationCard";
import { CreationModal } from "@/components/CreationModal";
import { GameCard } from "@/components/GameCard";

const socialIcons = { twitter: Twitter, instagram: Instagram, youtube: Youtube, twitch: Twitch, tiktok: Globe, website: Globe };

const DEPT_LABELS = { UGC: "UGC Creator", Builder: "Builder", Scripter: "Scripter", GFX: "GFX Artist", VFX: "VFX Artist" };

const DiscordIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.211.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.056c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028ZM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.332-.946 2.418-2.157 2.418Z" />
  </svg>
);

export default function CreatorDetail() {
  const { id } = useParams();
  const [creator, setCreator] = useState(null);
  const [creations, setCreations] = useState([]);
  const [games, setGames] = useState([]);
  const [site, setSite] = useState({});
  const [active, setActive] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/team/${id}`).then((r) => setCreator(r.data)).catch(() => setCreator(false));
    api.get(`/creations?creator_id=${id}`).then((r) => setCreations(r.data)).catch(() => {});
    api.get("/games").then((r) => setGames(r.data.filter((g) => g.creator_id === id))).catch(() => {});
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, [id]);

  if (creator === false)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-display text-2xl uppercase">Creator not found</p>
        <Link to="/" className="font-mono text-xs tracking-[0.2em] uppercase text-volt">← Back home</Link>
      </div>
    );

  return (
    <div className="relative">
      <Header />
      {creator && (
        <>
          <section className="relative min-h-[70vh] flex items-end overflow-hidden">
            <img src={creator.avatar_url || "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=1600"} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/40" />
            <div className="absolute inset-0 grid-lines opacity-40" />
            <div className="relative max-w-[1600px] w-full mx-auto px-6 md:px-10 pb-16 pt-32">
              <Link to="/#creators" data-testid="back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-volt transition-colors duration-200 mb-8">
                <ArrowLeft className="w-4 h-4" /> All creators
              </Link>
              <div className="flex items-end gap-6">
                <img src={creator.avatar_url || "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400"} alt={creator.name}
                  className="w-24 h-24 md:w-32 md:h-32 object-cover border border-border shrink-0" />
                <div>
                  <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">
                    {(creator.tier === "Lead" ? "Lead " : "") + (DEPT_LABELS[creator.department] || "Creator")} · @{creator.roblox_username || "roblox"}
                  </span>
                  <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter uppercase mt-3 leading-[0.9]">
                    {creator.name}
                  </motion.h1>
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-12 md:py-16">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Connect</span>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {creator.roblox_url && (
                <a href={creator.roblox_url} target="_blank" rel="noreferrer" data-testid="creator-roblox-profile"
                  className="group inline-flex items-center gap-2.5 border border-border bg-card px-5 py-3 hover:border-volt transition-colors duration-200">
                  <Gamepad2 className="w-4 h-4 text-volt" />
                  <span className="font-mono text-xs tracking-[0.15em] uppercase">Roblox Profile</span>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-volt transition-colors duration-200" />
                </a>
              )}
              {creator.discord_url && (
                <a href={creator.discord_url} target="_blank" rel="noreferrer" data-testid="creator-discord-comms"
                  className="group inline-flex items-center gap-2.5 bg-volt text-white px-5 py-3 hover:brightness-110 transition-all duration-200">
                  <DiscordIcon className="w-4 h-4" />
                  <span className="font-mono text-xs tracking-[0.15em] uppercase">Join Their Comms</span>
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              )}
              {creator.portfolio_url && (
                <a href={creator.portfolio_url} target="_blank" rel="noreferrer" data-testid="creator-portfolio-link"
                  className="group inline-flex items-center gap-2.5 border border-border bg-card px-5 py-3 hover:border-volt transition-colors duration-200">
                  <Globe className="w-4 h-4 text-volt" />
                  <span className="font-mono text-xs tracking-[0.15em] uppercase">Portfolio</span>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-volt transition-colors duration-200" />
                </a>
              )}
              {Object.entries(creator.socials || {}).filter(([, v]) => v).map(([k, v]) => {
                const Icon = socialIcons[k] || Globe;
                return (
                  <a key={k} href={v} target="_blank" rel="noreferrer" data-testid={`social-${k}`}
                    className="w-12 h-12 flex items-center justify-center border border-border hover:border-volt hover:text-volt transition-colors duration-200">
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </section>

          {games.length > 0 && (
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-8">
              <h2 className="font-display font-bold text-2xl sm:text-4xl tracking-tight uppercase mb-12">Live Games</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {games.map((g, i) => <GameCard key={g.id} game={g} index={i} />)}
              </div>
            </section>
          )}

          <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 pb-24">
            <h2 className="font-display font-bold text-2xl sm:text-4xl tracking-tight uppercase mb-12">Portfolio</h2>
            {creations.length === 0 ? (
              <p className="text-muted-foreground font-mono text-sm">No creations yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] gap-6">
                {creations.map((c, i) => <CreationCard key={c.id} creation={c} index={i} onOpen={setActive} />)}
              </div>
            )}
          </section>
        </>
      )}
      <Footer site={site} />
      <CreationModal creation={active} onClose={() => setActive(null)} />
    </div>
  );
}
